/* global globalThis */
// Standalone, explicitly started WHIP/WHEP. Capture and app session debt belong
// to the account controller. A connected peer is not provider LIVE evidence.
const messages = {
  INVALID_INPUT: 'Invalid camera transport input.',
  INVALID_RESOURCE: 'Invalid camera transport resource.',
  INVALID_ANSWER: 'Invalid camera transport answer.',
  HTTP_FAILED: 'Camera transport request failed.',
  CONNECTION_FAILED: 'Camera transport connection failed.',
  TIMEOUT: 'Camera transport timed out.',
  STOPPED: 'Camera transport stopped.',
};
const failure = code => Object.assign(new Error(messages[code]), { code });
function mediaURL(value) {
  const url = new URL(value);
  if (url.username || url.password || url.search || url.hash
    || !(url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)))) throw failure('INVALID_INPUT');
  return url;
}
function validSDP(sdp) {
  return typeof sdp === 'string' && sdp.length <= 1024 * 1024
    && /^v=0\r?\n/.test(sdp) && /\r?\nm=video /m.test(sdp);
}

export function createSelfHostedCameraTransport({
  role, authorizedMediaOrigin, videoOnly = false, recordingVideo = false,
  peerFactory = () => new RTCPeerConnection({ iceServers: [] }),
  MediaStream: Stream = globalThis.MediaStream,
  fetch: request = (...args) => globalThis.fetch(...args),
  negotiationTimeoutMs = 15000, deleteTimeoutMs = 2000,
} = {}) {
  let origin;
  try {
    const url = mediaURL(authorizedMediaOrigin);
    if (url.pathname !== '/' || !['publish', 'view'].includes(role)
      || !Number.isFinite(negotiationTimeoutMs) || negotiationTimeoutMs < 1 || negotiationTimeoutMs > 60000
      || !Number.isFinite(deleteTimeoutMs) || deleteTimeoutMs < 1 || deleteTimeoutMs > 10000) throw failure('INVALID_INPUT');
    origin = url.origin;
  } catch { throw failure('INVALID_INPUT'); }
  let current = null, generation = 0, disposed = false;
  let state = { status: 'idle', stream: null, error: null, generation: 0 };
  const listeners = new Set(), records = [];
  const snapshot = () => ({ ...state });
  const receipt = g => ({ generation: g.id, localPeerClosed: g.closed, remote: g.remote });
  const emit = (g, patch) => {
    // Disposal is already fenced when its final stopped notification runs.
    if (g !== current || (disposed && patch.status !== 'stopped')) return;
    state = { ...state, ...patch, generation: g.id };
    for (const listener of listeners) { try { listener(snapshot()); } catch { /* Consumer cannot interrupt teardown. */ } }
  };
  const listen = (g, target, event, fn) => {
    target.addEventListener(event, fn);
    const remove = () => target.removeEventListener(event, fn);
    g.removers.push(remove);
    return remove;
  };
  const active = g => !g.closed && current === g && !disposed;
  const check = g => { if (!active(g)) throw failure('STOPPED'); };
  const cancelBody = response => { try { Promise.resolve(response.body?.cancel()).catch(() => {}); } catch { /* Already consumed. */ } };
  function resource(g, response) {
    if (response.redirected || (response.url && response.url !== g.endpoint)) throw failure('INVALID_RESOURCE');
    const location = response.headers.get('location');
    if (!location) throw failure('INVALID_RESOURCE');
    let url;
    try { url = mediaURL(new URL(location, g.endpoint).href); } catch { throw failure('INVALID_RESOURCE'); }
    if (url.origin !== origin || !url.pathname.startsWith(new URL(g.endpoint).pathname + '/')
      || url.pathname === new URL(g.endpoint).pathname + '/') throw failure('INVALID_RESOURCE');
    return url.href;
  }
  // The Location carries the MediaMTX resource capability. No bearer is needed
  // by this pinned DELETE handler; never log or expose that capability in state.
  function removeResource(g) {
    if (!g.resource || g.deletion) return g.deletion || Promise.resolve(receipt(g));
    g.remote = 'unknown';
    const controller = new AbortController();
    g.deletion = new Promise(resolve => {
      let done = false;
      const finish = acknowledged => {
        if (done) return;
        done = true; clearTimeout(timer); controller.abort();
        if (acknowledged) g.remote = 'delete-acknowledged';
        g.resource = null;
        resolve(receipt(g));
      };
      const timer = setTimeout(() => finish(false), deleteTimeoutMs);
      Promise.resolve().then(() => request(g.resource, {
        method: 'DELETE', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer', signal: controller.signal,
      })).then(response => {
        cancelBody(response);
        finish(!response.redirected && (!response.url || response.url === g.resource) && response.status === 200);
      }, () => finish(false));
    });
    return g.deletion;
  }
  function close(g, status = 'stopped', code = null) {
    if (!g.closed) {
      g.closed = true;
      clearTimeout(g.timer);
      g.abort.abort();
      for (const remove of g.removers.splice(0)) remove();
      try { g.pc.close(); } catch { /* Browser close is idempotent. */ }
      // Only received tracks are owned here. Never stop the supplied capture.
      for (const track of g.received?.getTracks() || []) track.stop();
      g.cancel(failure(code || 'STOPPED'));
      emit(g, { status, error: code, stream: null });
    }
    return removeResource(g);
  }
  async function start({ endpoint, token, stream, audioOptIn = false } = {}) {
    // All authority/capture validation precedes peer allocation or dispatch.
    let url, tracks;
    try {
      url = mediaURL(endpoint);
      if (disposed || (current && !current.closed) || url.origin !== origin
        || !url.pathname.endsWith(role === 'publish' ? '/whip' : '/whep')
        || typeof token !== 'string' || !/^[A-Za-z0-9._~+/-]+=*$/.test(token) || token.length > 16384) throw failure('INVALID_INPUT');
      if (role === 'publish') {
        if (!stream || typeof stream.getTracks !== 'function') throw failure('INVALID_INPUT');
        tracks = stream.getTracks();
        if (!tracks.some(t => t.kind === 'video' && t.readyState === 'live')
          || tracks.some(t => !['video', 'audio'].includes(t.kind) || t.readyState !== 'live'
            || (t.kind === 'audio' && t.enabled && audioOptIn !== true))) throw failure('INVALID_INPUT');
      }
    } catch { throw failure('INVALID_INPUT'); }
    let pc;
    try { pc = peerFactory(); } catch { throw failure('CONNECTION_FAILED'); }
    const g = { id: ++generation, pc, endpoint, abort: new AbortController(), removers: [], closed: false,
      remote: 'not-created', resource: null, deletion: null, received: null, negotiated: false };
    current = g; records.push(g);
    const cancelled = new Promise((_resolve, reject) => { g.cancel = reject; });
    // Every asynchronous step races the same generation cancellation/deadline.
    const step = async promise => { const value = await Promise.race([promise, cancelled]); check(g); return value; };
    cancelled.catch(() => {});
    g.timer = setTimeout(() => close(g, 'error', 'TIMEOUT'), negotiationTimeoutMs);
    emit(g, { status: 'connecting', error: null, stream: null });
    try {
      check(g);
      listen(g, pc, 'connectionstatechange', () => {
        if (!active(g)) return;
        if (pc.connectionState === 'connected') {
          if (g.negotiated) clearTimeout(g.timer);
          emit(g, { status: 'connected' });
        } else if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) close(g, 'error', 'CONNECTION_FAILED');
      });
      if (role === 'publish') {
        tracks.forEach(track => pc.addTrack(track, stream));
        if (recordingVideo) {
          // The retained MP4 recorder cannot archive VP8. Negotiate the actual
          // publisher to H264; never let live-only VP8 masquerade as recordable.
          const codecs = globalThis.RTCRtpSender?.getCapabilities('video')?.codecs.filter(c => c.mimeType.toLowerCase() === 'video/h264');
          const transceiver = pc.getTransceivers().find(t => t.sender.track?.kind === 'video');
          if (!codecs?.length || !transceiver?.setCodecPreferences) throw failure('CONNECTION_FAILED');
          transceiver.setCodecPreferences(codecs);
        }
      }
      else {
        pc.addTransceiver('video', { direction: 'recvonly' });
        if (!videoOnly) pc.addTransceiver('audio', { direction: 'recvonly' });
        listen(g, pc, 'track', event => {
          if (!active(g) || !event.track) return;
          if (videoOnly && event.track.kind !== 'video') { event.track.stop(); close(g, 'error', 'CONNECTION_FAILED'); return; }
          if (!g.received) g.received = new Stream();
          if (!g.received.getTracks().includes(event.track)) g.received.addTrack(event.track);
          listen(g, event.track, 'ended', () => { if (active(g)) close(g, 'error', 'CONNECTION_FAILED'); });
          emit(g, { stream: g.received });
        });
      }
      const offer = await step(pc.createOffer());
      await step(pc.setLocalDescription(offer));
      if (pc.iceGatheringState !== 'complete') await step(new Promise(resolve => {
        const remove = listen(g, pc, 'icegatheringstatechange', () => {
          if (pc.iceGatheringState === 'complete') { remove(); resolve(); }
        });
        if (pc.iceGatheringState === 'complete') { remove(); resolve(); }
      }));
      check(g);
      const sdp = pc.localDescription?.sdp;
      if (!validSDP(sdp)) throw failure('INVALID_ANSWER');
      g.remote = 'unknown'; // Dispatch may allocate even if its ACK is lost.
      const pending = Promise.resolve().then(() => {
        check(g);
        return request(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/sdp', Authorization: 'Bearer ' + token },
          body: sdp, signal: g.abort.signal, credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer' });
      }).then(response => {
        // Observe late resource acquisition even after the cancellation race won.
        if (response.status === 201) {
          try { g.resource = resource(g, response); } catch (error) { cancelBody(response); throw error; }
        }
        if (!active(g)) { cancelBody(response); removeResource(g); throw failure('STOPPED'); }
        return response;
      });
      const response = await step(pending);
      if (response.redirected || (response.status >= 300 && response.status < 400)) { cancelBody(response); throw failure('INVALID_RESOURCE'); }
      if (response.status !== 201) { cancelBody(response); throw failure('HTTP_FAILED'); }
      if (response.headers.get('content-type')?.split(';')[0].trim().toLowerCase() !== 'application/sdp') { cancelBody(response); throw failure('INVALID_ANSWER'); }
      const answer = await step(response.text());
      if (!validSDP(answer)) throw failure('INVALID_ANSWER');
      try { await step(pc.setRemoteDescription({ type: 'answer', sdp: answer })); }
      catch (error) { if (!active(g)) throw error; throw failure('INVALID_ANSWER'); }
      g.negotiated = true;
      if (pc.connectionState === 'connected') clearTimeout(g.timer);
      return snapshot();
    } catch (error) {
      const code = messages[error?.code] ? error.code : 'HTTP_FAILED';
      if (active(g)) close(g, 'error', code);
      throw failure(code);
    }
  }
  function stop() {
    const captured = current;
    return captured ? close(captured).then(() => receipt(captured))
      : Promise.resolve({ generation: 0, localPeerClosed: true, remote: 'not-created' });
  }
  return {
    start, stop,
    dispose() { disposed = true; const result = stop(); listeners.clear(); return result; },
    getState: snapshot,
    getCleanup: () => records.map(receipt),
    subscribe(listener) { if (disposed) return () => {}; listeners.add(listener); return () => listeners.delete(listener); },
  };
}
